import React, { useEffect } from 'react';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import {
	extractInstruction,
	type Instruction,
} from '@atlaskit/side-nav-items/drag-and-drop/hitbox';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import {
	isL1ItemData,
	isL1ItemDataInGroup,
	isMoreMenuData,
} from '@atlassian/navigation-drag-and-drop/dnd-data';

import type { useDragAndDropSave } from '../use-drag-and-drop-save';

type UseMonitorForDnDElementsArgs = {
	dragAndDropSave: ReturnType<typeof useDragAndDropSave>['dragAndDropSave'];
	/** The IDs of menu items currently hidden in the More Menu. Owned by the caller. */
	hiddenMenuIds: string[];
	l1DragAndDropEnabled: boolean;
	setDraggingL1Group: (group: string | null) => void;
};

/**
 * `userMenuDataShapes.hiddenMenuIds` is stored in a ref that is updated on every render via
 * a layout-style effect (no dep array). The monitor closure reads from the ref instead of
 * capturing `hiddenMenuIds` directly, so the monitor is never torn down and re-registered
 * when `hiddenMenuIds` changes (e.g. after each drag-to-hide/show operation).
 */
export function useMonitorForDnDElements({
	l1DragAndDropEnabled,
	dragAndDropSave,
	setDraggingL1Group,
	hiddenMenuIds,
}: UseMonitorForDnDElementsArgs): void {
	// Keep a ref up to date with the latest hiddenMenuIds on every render.
	// This allows the onDrop closure to always read the current value without
	// needing hiddenMenuIds in the monitor's dependency array.
	const hiddenMenuIdsRef = React.useRef(hiddenMenuIds);
	useEffect(() => {
		hiddenMenuIdsRef.current = hiddenMenuIds;
	});

	useEffect(() => {
		if (!l1DragAndDropEnabled) {
			return;
		}
		return monitorForElements({
			canMonitor: ({ source }) =>
				isL1ItemData(source.data) &&
				isL1ItemDataInGroup(source.data, [L1_MENU_GROUPS.MENU, L1_MENU_GROUPS.SHORTCUTS]),
			onDragStart({ source }) {
				if (!isL1ItemData(source.data)) {
					return;
				}
				setDraggingL1Group(source.data.group);
			},
			onDrop({ source, location }) {
				const dragging = source.data;
				setDraggingL1Group(null);

				const [innerMost] = location.current.dropTargets;
				if (!innerMost) {
					return;
				}
				const dropTargetData = innerMost.data;

				const instruction: Instruction | null = extractInstruction(dropTargetData);
				if (!instruction) {
					return;
				}

				// Read from ref — always holds the latest hiddenMenuIds without causing
				// the monitor to re-register when the list changes.
				const hiddenMenuIds = hiddenMenuIdsRef.current;

				// Dragging out from More Menu
				if (
					isL1ItemData(dragging) &&
					isL1ItemData(dropTargetData) &&
					hiddenMenuIds.includes(dragging.menuItemId)
				) {
					dragAndDropSave({
						type: 'item-unhide',
						draggedMenuItemId: dragging.menuItemId,
						draggedMenuItemLabel: dragging.label,
						droppedOnMenuItemId: dropTargetData.menuItemId,
						instruction,
						trigger: 'pointer',
					});
					return;
				}

				// Drag and Drop Order within Shortcuts group
				if (
					isL1ItemData(dragging) &&
					isL1ItemData(dropTargetData) &&
					isL1ItemDataInGroup(dragging, L1_MENU_GROUPS.SHORTCUTS) &&
					isL1ItemDataInGroup(dropTargetData, L1_MENU_GROUPS.SHORTCUTS)
				) {
					dragAndDropSave({
						type: 'item-reorder',
						draggedMenuItemId: dragging.menuItemId,
						draggedMenuItemLabel: dragging.label,
						droppedOnMenuItemId: dropTargetData.menuItemId,
						instruction,
						trigger: 'pointer',
					});
					return;
				}

				// Drag and Drop Order within Menu group
				if (
					isL1ItemData(dragging) &&
					isL1ItemData(dropTargetData) &&
					isL1ItemDataInGroup(dragging, L1_MENU_GROUPS.MENU) &&
					isL1ItemDataInGroup(dropTargetData, L1_MENU_GROUPS.MENU)
				) {
					dragAndDropSave({
						type: 'item-reorder',
						draggedMenuItemId: dragging.menuItemId,
						draggedMenuItemLabel: dragging.label,
						droppedOnMenuItemId: dropTargetData.menuItemId,
						instruction,
						trigger: 'pointer',
					});
					return;
				}

				// Drag into More Menu (hide or show)
				if (
					isL1ItemData(dragging) &&
					isL1ItemDataInGroup(dragging, [L1_MENU_GROUPS.MENU, L1_MENU_GROUPS.SHORTCUTS]) &&
					isMoreMenuData(dropTargetData)
				) {
					dragAndDropSave({
						type: hiddenMenuIds.includes(dragging.menuItemId) ? 'item-visible' : 'item-hide',
						draggedMenuItemId: dragging.menuItemId,
						draggedMenuItemLabel: dragging.label,
						instruction,
						trigger: 'pointer',
					});
					return;
				}
			},
		});
		// Stable dep array: hiddenMenuIds is intentionally excluded — it is kept current via the
		// ref above. Only re-register the monitor when the save function or enabled flag changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dragAndDropSave, l1DragAndDropEnabled, setDraggingL1Group]);
}
